import { useState, useEffect } from "react";
import { useAuth } from "@/context/authContext";
import { getUserProfileByAuthId } from "@/Services/users.service";
import { isAdmin as checkIsAdmin, hasRole, RoleCode } from "@/models/user";
import type { User } from "@/models/user";

/**
 * Hook to manage user profile and permissions for ministers section
 */
export function useUserProfile() {
  const { user } = useAuth();
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user?.id) {
        setUserProfile(null);
        setIsLoading(false);
        return;
      }

      try {
        const profile = await getUserProfileByAuthId(user.id);
        setUserProfile(profile);
      } catch (err) {
        console.error("Error loading user profile:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserProfile();
  }, [user?.id]);

  const isAdmin = checkIsAdmin(userProfile);
  const isRegionAdmin = hasRole(userProfile, RoleCode.REGION_ADMIN);
  const isDistrictAdmin = hasRole(userProfile, RoleCode.DISTRICT_ADMIN);

  return {
    user,
    userProfile,
    isAdmin,
    isRegionAdmin,
    isDistrictAdmin,
    canEdit: isAdmin, // Only admin can edit ministers
    canViewHistory: isAdmin || isRegionAdmin || isDistrictAdmin,
    isLoading,
  };
}
